import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { createDirectorMessageSchema, type CreateDirectorMessageRequest } from '@tg-hookah/shared/dto';
import { useSendDirector } from './useSendDirector';
import { toast } from '@/components/Toast';
import { haptic } from '@/lib/tg';
import { Spinner } from '@/components/Spinner';

export function DirectorForm() {
  const { t } = useTranslation();
  const send = useSendDirector();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateDirectorMessageRequest>({
    resolver: zodResolver(createDirectorMessageSchema),
    defaultValues: { text: '' },
  });

  async function onSubmit(values: CreateDirectorMessageRequest) {
    try {
      await send.mutateAsync(values);
      haptic('success');
      toast(t('director.sent'), 'success');
      reset({ text: '' });
    } catch {
      haptic('error');
      toast(t('common.error'), 'error');
    }
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit(onSubmit)} noValidate>
      <textarea
        rows={6}
        placeholder={t('director.placeholder')}
        {...register('text')}
        className="w-full rounded-2xl bg-white/[0.03] text-text px-3.5 py-3 outline-none border border-white/10 focus:border-white/35 focus:bg-white/[0.05] transition-colors placeholder:text-white/30"
      />
      {errors.text?.message ? (
        <p role="alert" className="text-xs" style={{ color: 'var(--ember)' }}>
          {errors.text.message}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={isSubmitting || send.isPending}
        data-track="click:director-send"
        className="rounded-full py-3 font-display tracking-[0.08em] uppercase text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-transform active:scale-[0.98]"
        style={{
          background: '#ffffff',
          color: '#0a0a0b',
          boxShadow: '0 10px 26px -12px rgba(255,255,255,0.3)',
        }}
      >
        {isSubmitting || send.isPending ? <Spinner className="w-4 h-4" /> : null}
        {t('director.send')}
        <span aria-hidden className="font-mono text-[12px] opacity-60">↗</span>
      </button>
    </form>
  );
}
